import { useEffect, useRef, useState } from "react"
import { useLocation } from "wouter"
import { useSectionScrollStore } from "@/stores/sectionScrollStore"

const railLineBaseClass =
  "block h-px rounded-full bg-current transition-all duration-300 ease-out"

export function LeftSidebar() {
  const sections = useSectionScrollStore((s) => s.sections)
  const activeIndex = useSectionScrollStore((s) => s.activeSectionIndex)
  const [, navigate] = useLocation()

  const listRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [isExpanded, setIsExpanded] = useState(false)
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  const highlightedIndex = previewIndex ?? activeIndex

  useEffect(() => {
    itemRefs.current[highlightedIndex]?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }, [highlightedIndex])

  useEffect(() => {
    const list = listRef.current
    if (!list) return

    const stop = (e: WheelEvent) => e.stopPropagation()
    list.addEventListener("wheel", stop, { passive: true })
    return () => list.removeEventListener("wheel", stop)
  }, [])

  const openPopover = () => setIsExpanded(true)
  const closePopover = () => {
    setIsExpanded(false)
    setPreviewIndex(null)
  }

  return (
    <aside
      data-sidebar="left"
      className="pointer-events-none fixed inset-y-0 left-0 z-50 hidden w-32 flex-col px-3 py-20 md:flex lg:w-52 lg:px-6 lg:py-24"
    >
      <nav
        data-sidebar-nav
        className="pointer-events-auto flex flex-1 items-center"
        onMouseEnter={openPopover}
        onMouseLeave={closePopover}
        onFocusCapture={openPopover}
        onBlurCapture={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
          closePopover()
        }}
      >
        <div data-sidebar-selector className="relative flex items-center">
          <div data-sidebar-rail className="flex flex-col items-start gap-1.5 py-3">
            {sections.map((section, sectionIndex) => {
              const isActive = sectionIndex === activeIndex
              const isHighlighted = sectionIndex === highlightedIndex

              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => navigate(section.route)}
                  onMouseEnter={() => setPreviewIndex(sectionIndex)}
                  onFocus={() => setPreviewIndex(sectionIndex)}
                  data-nav-line
                  data-active={isActive || undefined}
                  data-highlighted={isHighlighted || undefined}
                  className={`group relative flex h-3 w-14 cursor-pointer items-center justify-start text-left text-foreground/38 outline-none transition-colors ${
                    isHighlighted ? "text-foreground/88" : "hover:text-foreground/72"
                  }`}
                  aria-label={section.label}
                  aria-current={isActive ? "true" : undefined}
                >
                  <span
                    data-nav-line-bar
                    className={railLineBaseClass}
                    style={{ width: isActive ? "2.75rem" : isHighlighted ? "1.9rem" : "1.1rem" }}
                  />
                </button>
              )
            })}
          </div>

          <div
            data-sidebar-popout
            className={`absolute top-1/2 left-full z-10 ml-5 -translate-y-1/2 transition-all duration-200 ease-out ${
              isExpanded
                ? "pointer-events-auto translate-x-0 opacity-100"
                : "pointer-events-none translate-x-2 opacity-0"
            }`}
          >
            <div
              data-sidebar-popout-panel
              className="w-52 rounded-2xl border border-border/60 bg-popover/78 p-3 shadow-2xl shadow-foreground/10 backdrop-blur-xl lg:w-64"
            >
              <div data-sidebar-popout-header className="px-2 pb-2">
                <span className="text-[10px] font-medium tracking-[0.22em] text-foreground/30 uppercase">
                  Sections
                </span>
              </div>

              <div
                ref={listRef}
                data-sidebar-popout-list
                className="max-h-[60vh] overflow-y-auto"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {sections.map((section, sectionIndex) => {
                  const isActive = sectionIndex === activeIndex
                  const isHighlighted = sectionIndex === highlightedIndex

                  return (
                    <button
                      key={section.id}
                      ref={(el) => { itemRefs.current[sectionIndex] = el }}
                      type="button"
                      onClick={() => navigate(section.route)}
                      onMouseEnter={() => setPreviewIndex(sectionIndex)}
                      onFocus={() => setPreviewIndex(sectionIndex)}
                      data-sidebar-popout-item
                      data-active={isActive || undefined}
                      data-highlighted={isHighlighted || undefined}
                      className={`flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors outline-none ${
                        isHighlighted ? "bg-accent/10" : "hover:bg-accent/6"
                      }`}
                    >
                      <span
                        data-sidebar-popout-line
                        className={`${railLineBaseClass} shrink-0 ${
                          isActive ? "text-foreground" : isHighlighted ? "text-foreground/72" : "text-foreground/24"
                        }`}
                        style={{ width: isActive ? "1.9rem" : isHighlighted ? "1.2rem" : "0.8rem" }}
                      />
                      <span
                        data-sidebar-popout-label
                        className={`min-w-0 truncate text-sm transition-colors ${
                          isActive
                            ? "font-semibold text-foreground"
                            : isHighlighted
                              ? "text-foreground/78"
                              : "text-foreground/45"
                        }`}
                      >
                        {section.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </aside>
  )
}
